/**
 * DietaryProfileContext — the diner's saved allergen/dietary card.
 *
 * Hydration rules:
 *   • Anonymous: load from localStorage `hp_dietary_profile_v1`.
 *   • Signed-in customer: load from /api/me/profile and overwrite.
 *     Writes via update() go to the server AND localStorage.
 *
 * Components consume via `useDietaryProfile()` to know what the
 * diner is sensitive to. The MenuPage, Recipe Detail, Cookbook
 * Browse, and Kitchen Display all share this state.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  loadGuestProfile, saveGuestProfile,
} from "@/lib/dietaryConflict";

const EMPTY = { allergens: [], dietary_tags: [], ailments: [],
                    dietary_notes: "" };

const DietaryProfileContext = createContext({
  profile:     EMPTY,
  allergens:   [],
  ailments:    [],
  legendAllergens: [],
  legendAilments:  [],
  loading:     false,
  update:      async () => {},
  reset:       () => {},
});


export function DietaryProfileProvider({ children }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(() => ({ ...EMPTY,
    ...loadGuestProfile() }));
  const [legendAllergens, setLA] = useState([]);
  const [legendAilments,  setLI] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch the canonical legends once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [aR, iR] = await Promise.all([
          api.get("/allergen-legend"),
          api.get("/ailment-legend"),
        ]);
        if (cancelled) return;
        setLA(aR.data || []);
        setLI(iR.data || []);
      } catch { /* keep empty defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hydrate from server when the customer signs in
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        if (cancelled) return;
        const next = {
          allergens:     data.allergens     || [],
          dietary_tags:  data.dietary_tags  || [],
          ailments:      data.ailments      || [],
          dietary_notes: data.dietary_notes || "",
        };
        setProfile(next);
        saveGuestProfile(next);   // mirror to LS for fast next-load
      } catch { /* keep current state */ }
      finally  { setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Update both locally + server (for customers only)
  const update = useCallback(async (patch) => {
    const next = { ...profile, ...patch };
    setProfile(next);
    saveGuestProfile(next);
    if (user && user.role === "customer") {
      try {
        await api.patch("/me/profile", patch);
      } catch { /* offline / non-fatal */ }
    }
  }, [profile, user]);

  const reset = useCallback(() => {
    setProfile(EMPTY);
    saveGuestProfile(EMPTY);
  }, []);

  return (
    <DietaryProfileContext.Provider value={{
      profile, allergens: profile.allergens, ailments: profile.ailments,
      legendAllergens, legendAilments,
      loading, update, reset,
    }}>
      {children}
    </DietaryProfileContext.Provider>
  );
}


export const useDietaryProfile = () => useContext(DietaryProfileContext);
